"use client";

import { motion, AnimatePresence } from "framer-motion";
import { getServices } from "./Header";
import { LanguageSelectorInline } from "./LanguageSelector";
import type { Locale } from "./templates/ServicePageTemplate";

const t = {
  sv: { services: "Tjänster", seePrice: "Se pris", business: "Företag", phone: "08-517 082 81", bookNow: "Boka nu" },
  en: { services: "Services", seePrice: "See price", business: "Business", phone: "08-517 082 81", bookNow: "Book now" },
};

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  locale?: Locale;
  bookingHref?: string;
}

export default function MobileNav({ open, onClose, locale = "sv", bookingHref }: MobileNavProps) {
  const localServices = getServices(locale);
  const l = t[locale];
  const href = bookingHref ?? (locale === "en" ? "/en/book" : "/boka");
  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[55] bg-black/20 lg:hidden"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute left-6 right-6 top-[80px] z-[60] bg-white lg:hidden rounded-2xl shadow-2xl"
          >
            <nav className="flex flex-col p-5 gap-1">
              <LanguageSelectorInline locale={locale} />
              <a
                href="tel:+46851708281"
                onClick={onClose}
                className="rounded-xl px-3 py-2 mt-2 text-[15px] font-semibold text-gray-800 hover:bg-gray-50 transition-colors border-2 border-brand-secondary text-center"
              >
                {l.phone}
              </a>
              <div className="my-3 border-t border-gray-100" />
              <p className="text-[15px] font-bold tracking-wider text-black px-3 mb-2">
                {l.services}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {localServices.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.name}
                      href={s.href}
                      onClick={onClose}
                      className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-[15px] font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                        style={{ backgroundColor: s.color + "14" }}
                      >
                        <Icon
                          className="h-[14px] w-[14px]"
                          style={{ color: s.color }}
                          strokeWidth={2}
                        />
                      </span>
                      {s.name}
                    </a>
                  );
                })}
              </div>

              <div className="mt-3 flex flex-col gap-2.5">
                <a
                  href={href}
                  onClick={onClose}
                  className="flex w-full items-center justify-center rounded-xl border-2 border-brand-secondary bg-white px-6 py-3.5 text-[15px] font-bold text-brand-secondary transition-all"
                >
                  {l.seePrice}
                </a>
                <a
                  href={href}
                  onClick={onClose}
                  className="flex w-full items-center justify-center rounded-xl bg-brand-secondary px-6 py-3.5 text-[15px] font-bold text-white hover:brightness-110 transition-all"
                >
                  {l.bookNow}
                </a>
              </div>

            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
